import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
} from "@material-ui/core";
import DirectionsCarOutlinedIcon from "@material-ui/icons/DirectionsCarOutlined";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import { useDispatch } from "react-redux";

import { OAuth as OAuthActions } from "../../js/actions";
import { setSnackbar } from "../../js/reducers/snackbar";
import { setAlertDialog } from "../../js/reducers/alertDialog";

const ACCENT = "#1d4ed8";
const ACCENT_DARK = "#1e40af";
const BORDER = "#e5e7eb";
const TEXT_TITLE = "#0f172a";
const TEXT_LABEL = "#94a3b8";
const TEXT_MUTED = "#64748b";
const SURFACE = "#ffffff";
const FIELD_BG = "#f1f5f9";

const MAX_FILE_BYTES = 200 * 1024 * 1024; // 200MB

const useStyles = makeStyles((theme) => ({
  wrapper: {
    padding: theme.spacing(1.5, 1.5),
    backgroundColor: SURFACE,
    fontFamily: theme.typography.fontFamily,
  },
  headerRow: {
    paddingBottom: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${BORDER}`,
  },
  headerTitle: {
    fontWeight: 700,
    color: TEXT_TITLE,
    fontSize: "1.1rem !important",
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
  },
  headerSubtitle: {
    color: TEXT_MUTED,
    fontSize: "0.78rem !important",
    fontWeight: 400,
    marginTop: 2,
  },

  summaryCard: {
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: SURFACE,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  summaryHeader: {
    padding: theme.spacing(1.25, 1.75),
    background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
  },
  summaryHeaderEmpty: {
    background: "#f3f4f6",
    color: TEXT_MUTED,
  },
  iconBubble: {
    width: 40,
    height: 40,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "white",
    flexShrink: 0,
  },
  iconFontSize: {
    fontSize: "22px !important",
  },
  iconBubbleEmpty: {
    backgroundColor: SURFACE,
    color: TEXT_MUTED,
    border: `1px solid ${BORDER}`,
  },
  summaryTitleBox: {
    minWidth: 0,
    flex: 1,
  },
  summaryKicker: {
    fontSize: "0.65rem !important",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    opacity: 0.85,
    lineHeight: 1,
  },
  summaryTitle: {
    fontSize: "0.95rem !important",
    fontWeight: 700,
    lineHeight: 1.25,
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  summaryBody: {
    padding: theme.spacing(1.5, 1.75),
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "flex-start",
    gap: theme.spacing(3),
    flex: 1,
    overflowX: "auto",
  },
  summaryBodyItem: {
    minWidth: 0,
    flex: "1 1 0",
  },
  summaryBodyEmpty: {
    padding: theme.spacing(2.5, 1.75),
    color: TEXT_MUTED,
    fontSize: "0.85rem !important",
  },
  summaryFieldLabel: {
    color: TEXT_LABEL,
    fontSize: "0.66rem !important",
    fontWeight: 500,
    textTransform: "uppercase",
    marginBottom: 2,
  },
  summaryFieldValue: {
    color: TEXT_TITLE,
    fontSize: "0.85rem !important",
    fontWeight: 600,
    lineHeight: 1.2,
    wordBreak: "break-word",
  },

  formSection: {
    marginTop: theme.spacing(2.5),
  },
  formTitle: {
    fontSize: "0.95rem !important",
    fontWeight: 700,
    color: TEXT_TITLE,
    marginBottom: theme.spacing(1.5),
  },

  fieldLabel: {
    color: TEXT_TITLE,
    fontSize: "0.85rem !important",
    fontWeight: 500,
    margin: 0,
    marginBottom: 4,
    lineHeight: 1.1,
  },
  required: {
    color: "#dc2626",
    fontWeight: 600,
    marginLeft: 2,
  },
  field: {
    margin: "0 !important",
    "&.MuiFormControl-root": {
      marginTop: "0 !important",
    },
    "& .MuiFilledInput-root": {
      borderRadius: 10,
      backgroundColor: FIELD_BG,
      border: "1px solid transparent",
      padding: "0 !important",
      transition:
        "background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
      "&:hover": {
        backgroundColor: "#e9eef5",
      },
      "&.Mui-focused": {
        backgroundColor: "white",
        borderColor: ACCENT,
        boxShadow: `0 0 0 3px rgba(29, 78, 216, 0.15)`,
      },
      "&::before, &::after": {
        display: "none",
        content: "none",
        border: "none",
      },
    },
    "& .MuiFilledInput-input, & .MuiInputBase-input, & .MuiInputBase-inputHiddenLabel, & .MuiSelect-select": {
      padding: "14px 12px !important",
      fontSize: "0.85rem !important",
      fontWeight: 400,
      color: TEXT_TITLE,
    },
  },

  fileField: {
    cursor: "pointer",
    "& .MuiFilledInput-root": {
      cursor: "pointer",
    },
    "& .MuiFilledInput-input": {
      cursor: "pointer",
    },
  },
  existingFileNote: {
    marginTop: theme.spacing(0.75),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    fontSize: "0.75rem !important",
    color: TEXT_MUTED,
  },
  emptyStateBanner: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    border: "1px solid #fde68a",
    backgroundColor: "#fffbeb",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  emptyStateIcon: {
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#f59e0b",
    flexShrink: 0,
  },
  emptyStateText: {
    fontSize: "0.82rem !important",
    color: "#92400e",
    fontWeight: 500,
    margin: 0,
    flex: 1,
  },
  emptyStateSubtext: {
    fontSize: "0.72rem !important",
    color: TEXT_LABEL,
    margin: "4px 0 0 0",
    lineHeight: 1.4,
  },
  existingFileLabel: {
    fontWeight: 600,
    color: TEXT_TITLE,
  },
  existingFileChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 8px",
    borderRadius: 999,
    backgroundColor: "#eff6ff",
    color: ACCENT_DARK,
    border: "1px solid #dbeafe",
    fontSize: "0.72rem !important",
    fontWeight: 600,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  uploadIconWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(1.2),
    marginTop: theme.spacing(1.5),
    border: `2px dashed ${BORDER}`,
    borderRadius: 12,
    backgroundColor: FIELD_BG,
    cursor: "pointer",
    transition: "all 200ms ease",
    "&:hover": {
      backgroundColor: "#e9eef5",
      borderColor: ACCENT,
    },
  },
  uploadIcon: {
    fontSize: "30px !important",
    color: TEXT_MUTED,
    transition: "color 200ms ease",
  },
  uploadIconHover: {
    color: ACCENT,
  },
  uploadText: {
    fontSize: "0.85rem !important",
    fontWeight: 500,
    color: TEXT_TITLE,
    margin: 0,
  },
  uploadSubtext: {
    fontSize: "0.72rem !important",
    color: TEXT_MUTED,
    margin: 0,
  },
  secondRow: {
    marginTop: theme.spacing(1.5),
  },

  actionsRow: {
    marginTop: theme.spacing(2.5),
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  primaryBtn: {
    color: "white",
    backgroundColor: ACCENT,
    textTransform: "none",
    fontSize: "0.85rem !important",
    fontWeight: 600,
    borderRadius: 8,
    border: `1px solid ${ACCENT}`,
    padding: theme.spacing(0.5, 2.25),
    minWidth: 84,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: ACCENT_DARK,
      borderColor: ACCENT_DARK,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#e2e8f0",
      color: "#94a3b8",
      border: "1px solid #cbd5e1",
    },
  },
  contentAvailabilityCard: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.9),
    border: `1px solid #10b981`,
    borderRadius: 12,
    backgroundColor: "#ecfdf5",
  },
  contentAvailabilityHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  contentAvailabilityTitle: {
    fontSize: "0.9rem !important",
    fontWeight: 700,
    color: "#065f46",
    margin: 0,
  },
  contentAvailabilityGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: theme.spacing(1.5),
  },
  contentAvailabilityItem: {
    display: "flex",
    flexDirection: "column",
  },
  contentAvailabilityLabel: {
    fontSize: "0.72rem !important",
    fontWeight: 600,
    color: "#059669",
    textTransform: "uppercase",
    marginBottom: 4,
    letterSpacing: "0.04em",
  },
  contentAvailabilityValue: {
    fontSize: "0.85rem !important",
    fontWeight: 500,
    color: "#065f46",
    wordBreak: "break-word",
  },
}));

const SummaryField = ({ label, value, classes }) => (
  <Box className={classes.summaryBodyItem}>
    <Box className={classes.summaryFieldLabel}>{label}</Box>
    <Typography className={classes.summaryFieldValue}>
      {value !== undefined && value !== null && value !== ""
        ? String(value)
        : "—"}
    </Typography>
  </Box>
);

const VehicleSummary = ({ vehicle, classes }) => {
  if (!vehicle) {
    return (
      <Box className={classes.summaryCard}>
        <Box className={`${classes.summaryHeader} ${classes.summaryHeaderEmpty}`}>
          <Box className={`${classes.iconBubble} ${classes.iconBubbleEmpty}`}>
            <DirectionsCarOutlinedIcon className={classes.iconFontSize} />
          </Box>
          <Box className={classes.summaryTitleBox}>
            <Box className={classes.summaryTitle}>No vehicle selected</Box>
          </Box>
        </Box>
        <Box className={classes.summaryBodyEmpty}>
          Select a vehicle on the Vehicle Details tab.
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.summaryCard}>
      <Box className={classes.summaryHeader}>
        <Box className={classes.iconBubble}>
          <DirectionsCarOutlinedIcon className={classes.iconFontSize} />
        </Box>
        <Box className={classes.summaryTitleBox}>
          <Box className={classes.summaryTitle}>
            {vehicle.modelName || "Unnamed Vehicle"}
          </Box>
        </Box>
      </Box>
      <Box className={classes.summaryBody}>
        <SummaryField
          label="Model Code"
          value={vehicle.modelCode}
          classes={classes}
        />
        <SummaryField
          label="Year"
          value={vehicle.yearOfManufacturing}
          classes={classes}
        />
        <SummaryField
          label="Variant"
          value={vehicle.variant}
          classes={classes}
        />
        <SummaryField label="Market" value={vehicle.market} classes={classes} />
      </Box>
    </Box>
  );
};

const ContentUpload = ({ selectedVehicle, onVehicleUpdated }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [language, setLanguage] = useState("");
  const [languageData, setLanguageData] = useState([]);
  const [referenceType, setReferenceType] = useState("");
  const [publicationTypes, setPublicationTypes] = useState([]);
  const [file, setFile] = useState(null);
  const [localPath, setLocalPath] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [isHoveringUpload, setIsHoveringUpload] = useState(false);
  const [checkingContent, setCheckingContent] = useState(false);
  const [contentAvailable, setContentAvailable] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadMeta = async () => {
      setLoadingMeta(true);
      try {
        const langResp = await dispatch(OAuthActions.getLanguage());
        if (!cancelled && langResp) {
          setLanguageData(Array.isArray(langResp) ? langResp : []);
        }
        const pubResp = await dispatch(OAuthActions.getPublicationTypes());
        if (!cancelled && pubResp) {
          setPublicationTypes(Array.isArray(pubResp) ? pubResp : []);
        }
      } catch (err) {
        // swallow; fields will simply have no options
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    };
    loadMeta();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Check content availability when vehicle is selected
  useEffect(() => {
    let cancelled = false;

    const checkContent = async () => {
      const vehicleModelUid =
        selectedVehicle?.vehicleModelDetailsUid ||
        selectedVehicle?.uid ||
        "";

      if (!vehicleModelUid) {
        if (!cancelled) {
          setContentAvailable(null);
        }
        return;
      }

      if (!cancelled) {
        setCheckingContent(true);
      }

      try {
        const result = await dispatch(OAuthActions.checkContentAvailability(vehicleModelUid));
        if (!cancelled) {
          setContentAvailable(result);
          console.log("Content availability result:", result);
        }
      } catch (err) {
        console.error("Error checking content availability:", err);
        if (!cancelled) {
          setContentAvailable({ isAvailable: false, message: "Error checking content" });
        }
      } finally {
        if (!cancelled) {
          setCheckingContent(false);
        }
      }
    };

    checkContent();

    return () => {
      cancelled = true;
    };
  }, [selectedVehicle, dispatch]);

  const vehicleUid =
    selectedVehicle?.uid ||
    selectedVehicle?.vehicleModelDetailsUid ||
    "";

  const handleFileChange = (event) => {
    const picked = event.target.files && event.target.files[0];
    if (!picked) return;
    if (picked.size > MAX_FILE_BYTES) {
      dispatch(
        setSnackbar(
          true,
          "error",
          "File size exceeds 200MB limit. Please pick a smaller file."
        )
      );
      event.target.value = "";
      setFile(null);
      setLocalPath("");
      return;
    }
    const sizeMb = (picked.size / (1024 * 1024)).toFixed(2);
    setFile(picked);
    setLocalPath(`${picked.name} (${sizeMb} MB)`);
  };

  const canSubmit =
    !!language &&
    !!referenceType &&
    !!file &&
    !!selectedVehicle &&
    !submitting;

  const handleUpload = async () => {
    if (!canSubmit) return;
    if (!vehicleUid) {
      dispatch(
        setSnackbar(
          true,
          "error",
          "Selected vehicle is missing an identifier. Please re-select a vehicle."
        )
      );
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await dispatch(
        OAuthActions.uploadDoc(language, vehicleUid, referenceType, formData)
      );
      if (response && response.error === undefined) {
        dispatch(setSnackbar(true, "success", "Upload document Successful"));
        const uploadedName = file?.name || "";
        if (typeof onVehicleUpdated === "function") {
          onVehicleUpdated({ uploadedFileName: uploadedName });
        }
        setFile(null);
        setLocalPath("");
      } else {
        dispatch(
          setAlertDialog(
            true,
            null,
            "error",
            "Document Upload",
            "Document upload was unsuccessful.\nDetails: " +
            (response && response.message
              ? response.message
              : "Unknown error"),
            "OK",
            ""
          )
        );
      }
    } catch (err) {
      dispatch(
        setSnackbar(
          true,
          "error",
          "Unable to upload the document. Please try again."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.headerRow}>
        <Typography className={classes.headerTitle}>Content Upload</Typography>
        <Typography className={classes.headerSubtitle}>
          Review the selected vehicle and upload the reference content.
        </Typography>
      </Box>

      <Grid container>
        <Grid item xs={12} md={6}>
          <VehicleSummary vehicle={selectedVehicle} classes={classes} />
        </Grid>
      </Grid>

      <Box className={classes.formSection}>
        <Typography className={classes.formTitle}>Upload Details</Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4} md={3}>
            <Box className={classes.fieldLabel}>
              Language <span className={classes.required}>*</span>
            </Box>
            <TextField
              fullWidth
              select
              variant="filled"
              hiddenLabel
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: { borderRadius: 12 },
                  },
                },
              }}
              disabled={loadingMeta}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {languageData.map((lang) => (
                <MenuItem key={lang.uid} value={lang.uid}>
                  {lang.language}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Box className={classes.fieldLabel}>
              Publication Type <span className={classes.required}>*</span>
            </Box>
            <TextField
              fullWidth
              select
              variant="filled"
              hiddenLabel
              value={referenceType}
              onChange={(e) => setReferenceType(e.target.value)}
              className={classes.field}
              InputProps={{ disableUnderline: true }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: { borderRadius: 12 },
                  },
                },
              }}
              disabled={loadingMeta}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {publicationTypes.map((type) => (
                <MenuItem key={type.uid} value={type.uid}>
                  {type.type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <Box className={classes.secondRow}>
              <Box className={classes.fieldLabel}>
                Content Upload <span className={classes.required}>*</span>
              </Box>
              <input
                accept=".zip,.rar,.7zip"
                style={{ display: "none" }}
                id="vij-content-upload-input"
                type="file"
                onChange={handleFileChange}
              />
              <label
                htmlFor="vij-content-upload-input"
                style={{ display: "block", cursor: "pointer", margin: 0 }}
              >
                <Box
                  className={classes.uploadIconWrapper}
                  onMouseEnter={() => setIsHoveringUpload(true)}
                  onMouseLeave={() => setIsHoveringUpload(false)}
                >
                  <CloudUploadOutlinedIcon
                    className={`${classes.uploadIcon} ${isHoveringUpload ? classes.uploadIconHover : ""
                      }`}
                  />
                  <Typography className={classes.uploadText}>
                    {localPath ? localPath : "Click to upload"}
                  </Typography>
                </Box>
              </label>
              {selectedVehicle && contentAvailable && !contentAvailable.isAvailable ? (
                <Box className={classes.emptyStateBanner}>
                  <Typography className={classes.emptyStateText}>
                    No content has been uploaded for this vehicle yet.
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            {contentAvailable && contentAvailable.isAvailable && contentAvailable.data ? (
              <Box className={classes.secondRow}>
                <Box className={classes.fieldLabel}>
                  Previously Uploaded Content
                </Box>
                <Box className={classes.contentAvailabilityCard}>
                  <Box className={classes.contentAvailabilityGrid}>
                    <Box className={classes.contentAvailabilityItem}>
                      <Box className={classes.contentAvailabilityLabel}>File Name</Box>
                      <Typography className={classes.contentAvailabilityValue}>
                        {contentAvailable.data.uploadedFileName}
                      </Typography>
                    </Box>
                    <Box className={classes.contentAvailabilityItem}>
                      <Box className={classes.contentAvailabilityLabel}>Language</Box>
                      <Typography className={classes.contentAvailabilityValue}>
                        {contentAvailable.data.language}
                      </Typography>
                    </Box>
                    <Box className={classes.contentAvailabilityItem}>
                      <Box className={classes.contentAvailabilityLabel}>Publication Type</Box>
                      <Typography className={classes.contentAvailabilityValue}>
                        {contentAvailable.data.publicationType}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : null}
          </Grid>
        </Grid>

        <Box className={classes.actionsRow}>
          <Button
            className={classes.primaryBtn}
            variant="contained"
            disableElevation
            onClick={handleUpload}
            disabled={!canSubmit}
            startIcon={
              submitting ? (
                <CircularProgress size={16} style={{ color: "white" }} />
              ) : null
            }
          >
            {submitting ? "Uploading..." : "Upload"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ContentUpload;
